// RPSModal.jsx
import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Modal, Spin, Button, message, Card, Table } from "antd";
import http from "@/utils/http";
import api from "@/utils/api";

const RPSModal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [monthData, setMonthData] = useState([]);
  const [moreLoading, setMoreLoading] = useState(false);
  const [nextMonthOffset, setNextMonthOffset] = useState(2);
  const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const formatVacancyRate = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${(value * 100).toFixed(2)}%`;
  };
  const formatVacancyCount = (count) => {
    if (count === null || count === undefined || count === "") return "-";
    return count;
  };

  useImperativeHandle(ref, () => ({
    open: (params = {}) => {
      setVisible(true);
      fetchData();
    },
    close: () => {
      setVisible(false);
      resetState();
    }
  }));

  const resetState = () => {
    setMonthData([]);
    setMoreLoading(false);
    setNextMonthOffset(2);
  };

  const buildRows = (monthData) => {
    const { year, month, weeks } = monthData || {};
    if (!weeks || !Array.isArray(weeks)) return [];

    const rows = [];
    
    //  反转周数组：从最后一周开始（月底在前）
    const reversedWeeks = [...weeks].reverse();
    
    reversedWeeks.forEach((week, weekIndex) => {
      if (!Array.isArray(week)) return;
      
      const dataRow = {
        key: `${year}_${month}_${weekIndex}`,
        isDataRow: true,
      };

      // 从周六到周日（月底在前）
      const reversedWeek = [...week].reverse();
      
      reversedWeek.forEach((dayData, idx) => {
        // 处理日期
        if (dayData && dayData.date) {
          const dateArray = dayData.date;
          const dateObj = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
          dataRow[`day_${idx}_date`] = dateObj.toLocaleDateString('en-US', { 
            month: '2-digit', 
            day: '2-digit', 
            year: 'numeric' 
          });
        } else {
          dataRow[`day_${idx}_date`] = "";
        }

        // 处理空缺数量
        if (dayData && dayData.vacancyCount !== undefined && dayData.vacancyCount !== null) {
          dataRow[`day_${idx}_count`] = formatVacancyCount(dayData.vacancyCount);
        } else {
          dataRow[`day_${idx}_count`] = " ";
        }

        // 处理空缺率
        if (dayData && dayData.vacancyRate !== undefined && dayData.vacancyRate !== null) {
          dataRow[`day_${idx}_rate`] = formatVacancyRate(dayData.vacancyRate);
        } else {
          dataRow[`day_${idx}_rate`] = " ";
        }
      });

      rows.push(dataRow);
    });

    return rows;
  };

  const buildMonthData = (monthsData) => {
    if (!monthsData || !Array.isArray(monthsData)) return [];

    // 月份从新到旧排序
    return [...monthsData]
      .sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return b.month - a.month;
      })
      .map((monthData) => ({
        key: `${monthData.year}_${monthData.month}`,
        monthName: monthData.monthName,
        rows: buildRows(monthData),
      }))
      .filter((monthTable) => monthTable.rows.length > 0);
  };

  const processData = (data) => {
    const { months, nextMonthOffset: nextOffset } = data;
    
    if (months && Array.isArray(months)) {
      const newMonthData = buildMonthData(months);
      setMonthData(newMonthData);
    }
    
    setNextMonthOffset(nextOffset || 2);
  };

  const fetchData = async () => {
    setLoading(true);
    setMonthData([]);
    setNextMonthOffset(2);
    
    try {
      const res = await http.get(api.getShowVacancyHistory, { 
        params: {} 
      });      
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }

      const data = res.data;
      if (!data) return;
      
      processData(data);
      
    } catch (error) {
      console.error("Failed to load vacancy history report:", error);
      message.error("Failed to load data, please try again later");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMonths = async () => {
    if (moreLoading) return;
    
    setMoreLoading(true);
    try {
      const requestParams = {
        monthOffset: nextMonthOffset,
      };
      const res = await http.get(api.getShowVacancyHistory, { 
        params: requestParams 
      });
      
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }

      const data = res.data;
      if (!data) return;
      
      const { months, nextMonthOffset: nextOffset } = data;
      
      if (months && Array.isArray(months)) {
        const newMonthData = buildMonthData(months);
        if (newMonthData.length > 0) {
          setMonthData(prevData => [...prevData, ...newMonthData]);
        }
      }
      
      setNextMonthOffset(nextOffset || 2);
    } catch (error) {
      console.error("Failed to load more months:", error);
      message.error(error?.response?.data?.msg || error?.msg || "Request failed");
    } finally {
      setMoreLoading(false);
    }
  };

  // 更新列标题为倒序显示（从周六到周日）
  const reversedWeekDays = [...weekDays].reverse();

const columns = reversedWeekDays.map((day, idx) => ({
  title: day,
  key: `day_${idx}`,
  align: "center",
  width: 150,
  render: (_, record) => {
    const date = record[`day_${idx}_date`] || "";
    const count = record[`day_${idx}_count`] || " ";
    const rate = record[`day_${idx}_rate`] || " ";
    return (
      <div className="rps-day-cell">
        <div className="rps-day-date">{date}</div>
        <div className="rps-day-count">{count}</div>
        <div className="rps-day-rate">{rate}</div>
      </div>
    );
  },
}));

return (
  <Modal
    title="Vacancy History Report"
    open={visible}
    onCancel={() => {
      setVisible(false);
      resetState();
    }}
    centered
    footer={null}
    maskClosable={false}
    width="70%"
    className="rps-modal"
    bodyStyle={{ padding: 0 }}
    destroyOnClose
  >
    <div className="rps-body">
      {loading ? (
        <div className="rps-status-wrap">
          <Spin size="large" />
          <div className="rps-status-text">Loading vacancy data...</div>
        </div>
      ) : monthData.length === 0 ? (
        <div className="rps-status-wrap">
          <div className="rps-empty-text">No data available</div>
        </div>
      ) : (
        <>
          {monthData.map((month) => (
            <div key={month.key} className="rps-month-block">
              <div className="rps-month-title">{month.monthName}</div>
              <Table
                className="rps-table"
                columns={columns}
                dataSource={month.rows}
                pagination={false}
                tableLayout="fixed"
                size="small"
                scroll={{ x: "max-content" }}
                rowClassName={() => "rps-data-row"}
                bordered
              />
            </div>
          ))}

          <div className="rps-load-more-wrap">
            <Button
              onClick={loadMoreMonths}
              loading={moreLoading}
              type="primary"
            >
              {moreLoading ? "Loading..." : "More"}
            </Button>
          </div>
        </>
      )}
    </div>
  </Modal>
);
});

export default RPSModal;